import type { LogoProduct } from "@allaria-tech/matter/Logo";

import { navigateToAllowedUrl } from ".";
import { CompanyType, SuccessRedirect } from "../types";

import bancoDelSolLogo from "../assets/images/logos/banco-del-sol.svg";

/**
 * Only linking reaches the preconfirmation. INVITACION and CONFORMIDAD both
 * open from another ALyC's data. INVITACION comes from a marketing email;
 * CONFORMIDAD has no link yet (TODO(backend)), so only `?variant=` mocks reach it.
 */
export type ConsentVariant =
  "APERTURA" | "VINCULACION" | "INVITACION" | "CONFORMIDAD";

/** The backend's name for the same split, fixed when the link is issued. */
export type ConsentKind = "TYC" | "VINCULATION";

/**
 * A kind outside this map means the contract moved: the caller errors out.
 * Only consulted for flows running more than one variant.
 */
export const variantByKind: Record<ConsentKind, ConsentVariant> = {
  TYC: "APERTURA",
  VINCULATION: "VINCULACION",
};

/**
 * Reported by the backend. `invalid_payload` is our bug, not the person's;
 * `comitentes_unavailable` (a 503 on accept) leaves the link alive.
 */
export type ConsentLinkReason =
  | "not_found"
  | "expired"
  | "superseded"
  | "already_used"
  // TODO(backend): name to confirm. Another representative already signed.
  | "already_completed"
  | "invalid_payload"
  | "comitentes_unavailable";

/** Only the ones they are **titular** of; backend excludes cotitular ones. */
export interface ConsentComitente {
  numero: string;
  nombre: string;
}

/** Personal data throughout: display only, never logged, never in a query string. */
export interface ConsentValidLink {
  valid: true;
  /** Which of the two screens this link opens. */
  kind: ConsentKind;
  dni: string;
  /** Null on an opening link; populated on a linking one. */
  cuit: string | null;
  /**
   * Looked up from the DNI during validation, which is why that call takes
   * seconds. Uppercased, and null on older links — the greeting copes.
   */
  first_name: string | null;
  last_name: string | null;
  /**
   * Linking only, and the states are **not** interchangeable: absent is an
   * opening link, a non-empty array is the only one the preconfirmation may
   * render, and `null` means the service is down — a retry, not an empty list.
   * `[]` (titular of none) is a dead end too, so it shares `null`'s screen.
   */
  comitentes?: ConsentComitente[] | null;
  /** For display. The countdown runs off the duration, never off this. */
  expires_at: string;
  expires_in_seconds: number;
}

export interface ConsentInvalidLink {
  valid: false;
  reason: ConsentLinkReason;
}

/** The 503 names itself under `error`, not `reason`. */
export interface ConsentUnavailable {
  error: ConsentLinkReason;
}

export interface ConsentAcceptedLink {
  valid: true;
  id: number;
  dni: string;
  /** Linking only: the comitente that was linked. */
  account_number?: string;
}

/** The company opening the account: its build serves the route, its brand heads the page. */
export interface ConsentHost {
  company: CompanyType;
  /** As written in "Te damos la bienvenida a …". */
  name: string;
  logo: LogoProduct;
}

/**
 * Everything that differs between partners. Adding one is an entry here;
 * screens, copy, state machine and API calls are all shared.
 */
export interface PartnerConsentFlow {
  /** Stable id, used for Sentry tags. */
  key: string;
  /** Display name, interpolated into every screen's copy. */
  name: string;
  /** Segment the links land on; the generator appends `/{token}`. */
  route: string;
  host: ConsentHost;
  /**
   * `token`: `/{route}/{token}`, validated against the backend before anything
   * renders. `params`: `/{route}?uuid=…&token=…&comitente=…&razon_social=…`, a
   * midgard account request with nothing to validate up front.
   */
  link: "token" | "params";
  /** Shown beside the host's. An ALyC handing over its own clients doesn't co-brand. */
  logo?: string;
  /** The partner's own links API. Each partner names its own env var. */
  baseUrl: string;
  /** Flows this partner runs; the first is the fallback while the backend
   * response doesn't say which one a link opens. */
  variants: ConsentVariant[];
  /**
   * Recorded as `document_version` on acceptance. It names the host company's
   * own terms — the same PDFs as the onboarding — so replacing those moves this.
   */
  openingDocumentVersion: string;
  /** Linking's conformity is a different document, so it needs its own version. */
  linkingDocumentVersion?: string;
  supportEmail: string;
  /** PDFs the ALyC variants' checkboxes link to; a missing one leaves plain text. */
  documents?: {
    terms?: string;
    dataTransfer?: string;
    managementRegulation?: string;
  };
  successRedirect?: SuccessRedirect;
  /**
   * `params` links only: once midgard accepts, the request is registered on
   * the FAL election sheet so the welcome email's fund choice can find it.
   */
  registersFalRequest?: boolean;
}

export const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const allaria: ConsentHost = {
  company: "ALLARIA",
  name: "Allaria",
  logo: "allaria",
};

const allariaFondos: ConsentHost = {
  company: "ALLARIAFONDOS",
  name: "Allaria Fondos",
  logo: "fondos",
};

export const partnerConsentFlows: PartnerConsentFlow[] = [
  {
    key: "bancodelsol",
    name: "Banco del Sol",
    route: "banco-del-sol",
    host: allaria,
    link: "token",
    logo: bancoDelSolLogo,
    baseUrl: import.meta.env.VITE_BASE_URL_BDS,
    variants: ["APERTURA", "VINCULACION"],
    // Agreed with backend 2026-09-17. Deliberately coarse: they identify the
    // document *set* each variant accepts, not a revision of any one PDF.
    openingDocumentVersion: "v1-tyc",
    linkingDocumentVersion: "v1-vinc",
    // TODO(design): placeholder from the Figma frames.
    supportEmail: "soporte@mail.com",
    successRedirect: {
      buttonText: "Ir a Banco del Sol",
      message: (s) =>
        `Por favor, no cierres esta ventana hasta que te redireccionemos a Banco del sol. 
Te redirigiremos a Banco Del Sol en ${s} segundo${s !== 1 ? "s" : ""}...`,
      autoRedirectMs: 5000,
      // Exact hosts, not `go.link`: that domain serves every Adjust customer.
      // TODO: add the production link's host once Banco del Sol sends it.
      allowedDomains: ["bancodelsol.com.ar", "bds-uat.go.link"],
      // Adjust link into their app; each environment has its own.
      defaultUrl: import.meta.env.VITE_BDS_APP_URL,
    },
  },
  {
    // Allaria's own comitentes opening at Allaria Fondos, from an email CTA.
    key: "allaria-fal",
    name: "Allaria",
    route: "fal",
    host: allariaFondos,
    link: "params",
    // Midgard: accepting goes to its account request notifications.
    baseUrl: import.meta.env.VITE_BASE_URL,
    variants: ["INVITACION", "CONFORMIDAD"],
    // Not sent: midgard's accept takes no body.
    openingDocumentVersion: "v1-conf",
    supportEmail: "soporte@allariafondos.com.ar",
    registersFalRequest: true,
    documents: {
      terms: `${import.meta.env.VITE_BASE_URL_STATIC}/terms/Terminos%20y%20Condiciones%20FAL.pdf`,
      // TODO(legales): PDFs of the data transfer and the Reglamento de gestión.
    },
  },
];

export const getPartnerConsentFlow = (key: string) =>
  partnerConsentFlows.find((flow) => flow.key === key);

/**
 * Back to the partner with nothing appended: they ask the signatures API
 * whether the person signed instead of reading it off the URL.
 */
export const leaveToPartner = (flow: PartnerConsentFlow, redirectUrl: string) =>
  navigateToAllowedUrl(redirectUrl, flow.successRedirect?.allowedDomains ?? []);
